import React, { useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppContext from "../context/AppProvider";
import {
  todayStr,
  getDaysInMonth,
  getFirstDayOfMonth,
  getDateStr,
  MONTH_NAMES,
  DAY_NAMES,
} from "../lib/dateUtils";
import { calcStreak, calcBestStreak } from "../lib/habitUtils";
import { IoArrowBack, IoChevronBack, IoChevronForward } from "react-icons/io5";
import { GiTrashCan } from "react-icons/gi";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const HabitPage = () => {
  const { id } = useParams();
  const { habits, deleteHabit, toggleEntry } = useContext(AppContext);
  const navigate = useNavigate();
  const habit = habits.find((h) => h.id === id);
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  if (!habit) {
    return (
      <div className="page">
        <button className="icon-btn" onClick={() => navigate("/")}>
          <IoArrowBack />
        </button>
        <p className="empty-msg" style={{ marginTop: 40 }}>Habit not found.</p>
      </div>
    );
  }

  const todayDateStr = todayStr();
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete "${habit.name}"?`)) {
      deleteHabit(id);
      navigate("/");
    }
  };

  const handleDayClick = (dayNum) => {
    const dateStr = getDateStr(viewYear, viewMonth, dayNum);
    if (dateStr > todayDateStr) return; // no future dates
    toggleEntry(habit.id, dateStr);
  };

  // Stats for the currently viewed month
  const done = days.filter(
    (d) => habit.entries[getDateStr(viewYear, viewMonth, d)] === "done"
  ).length;
  const missed = days.filter(
    (d) => habit.entries[getDateStr(viewYear, viewMonth, d)] === "missed"
  ).length;
  const total = done + missed;
  const rate = total > 0 ? Math.round((done / total) * 100) : 0;

  // Last 6 months for bar chart
  const last6 = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const y = d.getFullYear();
    const m = d.getMonth();
    const dim = getDaysInMonth(y, m);
    const monthDays = Array.from({ length: dim }, (_, j) => j + 1);
    const doneCount = monthDays.filter(
      (day) => habit.entries[getDateStr(y, m, day)] === "done"
    ).length;
    const missedCount = monthDays.filter(
      (day) => habit.entries[getDateStr(y, m, day)] === "missed"
    ).length;
    return { label: MONTH_NAMES[m].slice(0, 3), done: doneCount, missed: missedCount };
  });

  const chartData = {
    labels: last6.map((m) => m.label),
    datasets: [
      {
        label: "Done",
        data: last6.map((m) => m.done),
        backgroundColor: habit.color,
        borderRadius: 6,
      },
      {
        label: "Missed",
        data: last6.map((m) => m.missed),
        backgroundColor: "#555",
        borderRadius: 6,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: "top" } },
    scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
  };

  return (
    <div className="page">
      <div className="habit-header">
        <button className="icon-btn" onClick={() => navigate("/")}>
          <IoArrowBack />
        </button>
        <h1 className="habit-title" style={{ color: habit.color }}>
          {habit.name}
        </h1>
        <button className="icon-btn danger" onClick={handleDelete}>
          <GiTrashCan />
        </button>
      </div>

      <div className="month-nav">
        <button className="nav-btn" onClick={prevMonth}>
          <IoChevronBack />
        </button>
        <h2 className="month-label">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </h2>
        <button className="nav-btn" onClick={nextMonth}>
          <IoChevronForward />
        </button>
      </div>

      <div className="calendar-grid habit-calendar">
        {DAY_NAMES.map((d) => (
          <div key={d} className="day-header">{d}</div>
        ))}
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`e${i}`} className="day-cell empty" />
        ))}
        {days.map((dayNum) => {
          const dateStr = getDateStr(viewYear, viewMonth, dayNum);
          const isToday = dateStr === todayDateStr;
          const isFuture = dateStr > todayDateStr;
          const status = habit.entries[dateStr];
          return (
            <div
              key={dayNum}
              className={`day-cell habit-day${isFuture ? " future" : ""}`}
              style={{
                backgroundColor:
                  status === "done"
                    ? habit.color
                    : status === "missed"
                    ? "#333"
                    : "",
                boxShadow: isToday ? `0 0 0 2px ${habit.color}` : "",
                cursor: isFuture ? "default" : "pointer",
              }}
              onClick={() => handleDayClick(dayNum)}
            >
              <span
                className="day-num"
                style={{
                  color: status ? "white" : isToday ? habit.color : "",
                  fontWeight: isToday ? 700 : 400,
                }}
              >
                {dayNum}
              </span>
            </div>
          );
        })}
      </div>

      <div className="legend">
        <span className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: habit.color }} /> Done
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: "#333" }} /> Missed
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: "#eee", border: "1px solid #ccc" }} /> Unmarked
        </span>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value" style={{ color: habit.color }}>
            {calcStreak(habit)}
          </div>
          <div className="stat-label">Current Streak</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: habit.color }}>
            {calcBestStreak(habit)}
          </div>
          <div className="stat-label">Best Streak</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: habit.color }}>{rate}%</div>
          <div className="stat-label">{MONTH_NAMES[viewMonth].slice(0, 3)} Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: habit.color }}>{done}</div>
          <div className="stat-label">Days Done</div>
        </div>
      </div>

      <div className="chart-container">
        <h3 className="section-title" style={{ marginBottom: 12 }}>
          Last 6 Months
        </h3>
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default HabitPage;
